// A shopper should never be the one to tell us a page broke. Fire-and-forget, deliberately plain
// fetch so a dead session or an axios interceptor can never turn a crash report into a redirect.
const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

const sent = new Set();
let budget = 3; // one broken page is a signal; fifty is a loop hammering the server

// Not every thrown error is our page breaking. In-app browsers (WhatsApp/Facebook webviews),
// browser extensions and third-party scripts (Google Maps, analytics) throw errors on our page
// that we can neither see the detail of nor fix. Logging them just spams the admin crash inbox.
const isThirdPartyNoise = ({ message = "", stack = "", filename = "" }) => {
  const m = String(message).trim();
  // Browsers redact any cross-origin script crash to this exact, detail-less string.
  if (/^Script error\.?$/i.test(m)) return true;
  // postMessage failures are raised by in-app browser bridges and embedded frames, never our code.
  if (/postMessage/i.test(m)) return true;
  const src = `${filename}\n${stack}`;
  // Injected extension scripts.
  if (/-extension:\/\//i.test(src)) return true;
  // A window-level error whose source file lives on another origin is someone else's script.
  if (filename && !filename.startsWith(window.location.origin)) return true;
  return false;
};

export const reportCrash = ({ message, stack = "", label = "", kind = "render", filename = "" }) => {
  if (isThirdPartyNoise({ message, stack, filename })) return;
  const msg = String(message || "Unknown error").slice(0, 400);
  const key = `${label}|${msg}`;
  if (sent.has(key) || budget <= 0) return;
  sent.add(key);
  budget -= 1;
  const token = localStorage.getItem("lp-token");
  fetch(`${API}/client-errors`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      message: msg,
      stack: String(stack || "").slice(0, 2000),
      label: String(label || "").slice(0, 60),
      path: `${window.location.pathname}${window.location.search}`.slice(0, 300),
      kind,
    }),
    keepalive: true,
  }).catch(() => {});
};

export const installCrashReporter = () => {
  window.addEventListener("error", (e) => {
    // Missing images and other resource errors aren't broken pages.
    if (!e?.error && !e?.message) return;
    reportCrash({ message: e.message || String(e.error), stack: e.error?.stack, filename: e.filename, kind: "window" });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e?.reason;
    if (!r) return;
    reportCrash({ message: r.message || String(r), stack: r.stack, kind: "promise" });
  });
};
